/**
 * Hydration / defaults / schema-evolution — framework-agnostic (no React imports) so the Astro loader
 * AND the builder reducer both call it. The registry is an explicit argument (kept React-free, no cycle).
 */
import { t } from '@aquilixshop/i18n';
import { nanoid } from 'nanoid';
import { FEATURED_GALLERY_SETTINGS, FEATURED_PRODUCT_GALLERY_VERSION, } from '../sections/featured-product/gallery.js';
/**
 * A preset value written `@t:some.key` is a sentence TO TRANSLATE, not a literal.
 *
 * `defaultKey` covers a field's own starting value, but a preset overrides that value with its own — the
 * COD buy-box preset ships three benefit bullets and four order-field labels that the field defaults never
 * see. Those live in the preset, so the preset needs a way to name a key too.
 *
 * The remainder must be dotted and lowercase, which is what makes the marker safe against a seller who
 * literally types `@t:hello` into a text field: no dot, no match, the string is kept verbatim.
 */
const TEXT_MARKER = /^@t:([a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+)$/;
/** defaults ∪ stored — stored wins; missing keys fall back to each field's default. */
export function applyDefaults(fields, stored, locale) {
    const lang = locale ?? 'fr';
    const out = {};
    for (const f of fields) {
        // A `defaultKey` wins over `default`: the starting value is a sentence in the SHOP's language rather
        // than in whichever one the schema was typed in. See SettingFieldBase.defaultKey.
        // A rich-text field holds `{format, html}`, not a bare string, so the translated sentence is wrapped
        // back into that shape — otherwise the editor would be handed a string and render nothing.
        if (f.defaultKey) {
            const phrase = t({ locale: lang }, f.defaultKey);
            out[f.id] = (f.type === 'richtext' ? { format: 'html', html: phrase } : phrase);
        }
        else if ('default' in f &&
            f.default !== undefined &&
            // A range whose UNSET state is a third state of its own (`inheritLabel`: the theme's ratio, the
            // automatic leading curve, 75 % of the desktop…) keeps NO stored default: its `default` is only
            // where the handle lands when the seller takes the field over (RangeControl), never a value. Stored,
            // it would freeze a newly added text block at 1.8 and hide the « Automatique » button for good — and
            // this runs at hydration too, so it would repaint every stored page's theme ratio as a literal.
            f.inheritLabel === undefined) {
            out[f.id] = f.default;
        }
    }
    const merged = { ...out, ...(stored ?? {}) };
    for (const id of Object.keys(merged)) {
        const value = merged[id];
        // Bare string, and the rich-text shape `{format, html}` — a preset heading is authored as rich text,
        // so its marker sits one level down and would otherwise render as the literal `@t:some.key`.
        if (typeof value === 'string') {
            const marker = TEXT_MARKER.exec(value);
            if (marker?.[1])
                merged[id] = t({ locale: lang }, marker[1]);
        }
        else if (value && typeof value === 'object' && 'html' in value) {
            const rich = value;
            if (typeof rich.html === 'string') {
                const marker = TEXT_MARKER.exec(rich.html);
                if (marker?.[1]) {
                    merged[id] = { ...rich, html: t({ locale: lang }, marker[1]) };
                }
            }
        }
    }
    return merged;
}
/**
 * Resolve a stored instance's settings for render:
 *   1. if instance.version < schema.version and migrate() exists, run migrate(instance.version, settings);
 *   2. then fill defaults for any missing field.
 * This is the ONLY working schema-evolution path: migrate needs the version that WROTE the blob, which is
 * exactly why `version` is stamped on the stored instance (not only on the schema).
 */
export function resolveSettings(schema, instance, locale) {
    const migrated = instance.version < schema.version && schema.migrate
        ? schema.migrate(instance.version, instance.settings)
        : instance.settings;
    return applyDefaults(schema.fields, migrated, locale);
}
/**
 * Build a fresh block instance for a section's repeatable child (builder 'add block').
 * Stamps version = schema.version, assigns a nanoid id, fills defaults from the block schema.
 * `overrides` lets a preset block supply explicit settings (else the block schema's `defaults`).
 *
 * Throws if `blockType` is not declared in `def.schema.blocks` — the reducer MUST validate the type
 * exists before calling this, so an invalid instance is never built (build-plan §5.2 / §11.4).
 */
export function instantiateBlock(def, blockType, overrides, locale) {
    const bschema = def.schema.blocks?.find((s) => s.type === blockType);
    if (!bschema) {
        throw new Error(`Section "${def.type}" declares no block of type "${blockType}".`);
    }
    return {
        id: nanoid(),
        type: blockType,
        version: def.schema.version,
        settings: applyDefaults(bschema.fields, overrides ?? bschema.defaults, locale),
    };
}
/**
 * Recursively instantiate a preset block node + its nested children (the Shrine static tree, e.g.
 * main-product → Gallery/Details → blocks). Each child type must be declared in `def.schema.blocks`.
 * Additive: a flat preset block (no `blocks`) instantiates exactly as before.
 */
function instantiateBlockNode(def, pb, locale) {
    const block = instantiateBlock(def, pb.type, pb.settings, locale);
    if (pb.static)
        block.static = true;
    if (pb.blocks && pb.blocks.length > 0) {
        block.blocks = pb.blocks.map((c) => instantiateBlockNode(def, c, locale));
    }
    return block;
}
/**
 * Build a fresh section instance from a definition + preset (builder 'add section' + theme seeding).
 * Stamps version = schema.version on the section AND every block, assigns nanoid ids, fills defaults.
 * Preset blocks may declare nested `blocks` → the whole tree is materialized recursively.
 */
export function instantiateSection(def, presetIndex = 0, locale) {
    if (def.presets.length === 0) {
        throw new Error(`Section "${def.type}" must declare at least one preset.`);
    }
    const preset = def.presets[presetIndex] ?? def.presets[0];
    const blocks = (preset?.blocks ?? []).map((b) => instantiateBlockNode(def, b, locale));
    return {
        id: nanoid(),
        type: def.type,
        version: def.schema.version,
        settings: applyDefaults(def.schema.fields, preset?.settings, locale),
        blocks,
    };
}
/**
 * Legacy per-block/section theme-color tokens renamed in feature 0019 §4a. Saved documents (authored
 * before the rename) carry these; they are remapped on EVERY hydrate — builder AND storefront (finding
 * #5) — so a live store never renders fallback colors and never needs a manual re-save.
 */
const TOKEN_RENAME = {
    brand: 'accent',
    line: 'border',
    lavender: 'accent',
    background: 'bg',
};
/**
 * Deep-walk a settings value, remapping any ColorValue whose theme `token` was renamed in 0019 §4a.
 * Returns the SAME reference when nothing changed, so unchanged subtrees don't churn React re-renders.
 * Independent of the store-level `migrateThemeSettings` (that migrates the palette; this migrates the
 * per-node section-tree tokens).
 */
function remapColorTokens(v) {
    if (Array.isArray(v)) {
        let changed = false;
        const arr = v.map((x) => {
            const r = remapColorTokens(x);
            if (r !== x)
                changed = true;
            return r;
        });
        return changed ? arr : v;
    }
    if (v && typeof v === 'object') {
        const o = v;
        if (typeof o.token === 'string' && TOKEN_RENAME[o.token]) {
            return { ...o, token: TOKEN_RENAME[o.token] };
        }
        let changed = false;
        const out = {};
        for (const k in o) {
            const r = remapColorTokens(o[k]);
            out[k] = r;
            if (r !== o[k])
                changed = true;
        }
        return changed ? out : v;
    }
    return v;
}
/**
 * Legacy `benefit-panel` → `benefit-row` CHILD blocks. Early panels stored up to 6 flat rows as
 * `icon<N>`/`icon<N>Fill`/`image<N>`/`text<N>` settings; the panel now hosts `benefit-row` child blocks
 * the seller manages in the tree. On EVERY hydrate (builder + storefront) a legacy panel with NO children
 * AND a non-empty flat row is upgraded in place — so old docs open with an editable row tree and never
 * need a manual re-save. Idempotent: a panel that already has children (or a fresh panel with no flat
 * rows) is returned UNCHANGED. The flat settings are left on the block (the component prefers children
 * when present), so a re-hydrate can never double-migrate.
 */
function migrateBenefitPanel(block) {
    if (block.type !== 'benefit-panel' || (block.blocks && block.blocks.length > 0))
        return block;
    const bs = block.settings;
    const rows = [];
    for (let n = 1; n <= 6; n++) {
        const text = bs[`text${n}`];
        if (typeof text !== 'string' || text.trim().length === 0)
            continue;
        const settings = { text };
        if (bs[`icon${n}`] !== undefined)
            settings.icon = bs[`icon${n}`];
        if (bs[`icon${n}Fill`] !== undefined)
            settings.iconFill = bs[`icon${n}Fill`];
        if (bs[`image${n}`] !== undefined)
            settings.image = bs[`image${n}`];
        rows.push({ id: nanoid(), type: 'benefit-row', version: block.version, settings });
    }
    return rows.length > 0 ? { ...block, blocks: rows } : block;
}
/**
 * Legacy `sizing-chart` table settings → `size-row` CHILD blocks. Before the child-row model the
 * table lived in settings: hidden `header1..4` plain fields (or the Shrine CSV `tableHeaderContent`
 * line, "[empty]" = blank cell) plus the `tableContent` richtext ("line per row, commas between
 * cells"). Those fields no longer exist in the inspector, so on EVERY hydrate a chart with NO
 * size-row child and legacy content is upgraded in place: the header cells become the FIRST row
 * (the header band), each richtext line a data row. Unlike benefit-panel, the legacy settings are
 * REMOVED — leaving them would resurrect a stale ghost table if the seller ever deleted all rows.
 * Idempotent: a chart that already has a size-row child (or has no legacy content) is unchanged.
 */
function migrateSizingChart(block) {
    if (block.type !== 'sizing-chart')
        return block;
    if ((block.blocks ?? []).some((b) => b.type === 'size-row'))
        return block;
    const bs = block.settings;
    const cell = (v) => (typeof v === 'string' ? v.trim() : '');
    const splitCsv = (raw) => raw.split(',').map((c) => {
        const t = c.trim();
        return t.toLowerCase() === '[empty]' ? '' : t;
    });
    // Headers: header1..4 win over the CSV line (the component's historical precedence).
    const plain = [cell(bs.header1), cell(bs.header2), cell(bs.header3), cell(bs.header4)];
    while (plain.length > 0 && !plain[plain.length - 1])
        plain.pop();
    const csv = cell(bs.tableHeaderContent);
    const headers = plain.length > 0 ? plain : csv ? splitCsv(csv) : [];
    // Rows: the tableContent richtext — each <p>/<br> line is a row, commas split cells.
    const rt = bs.tableContent;
    const html = rt?.format === 'html' && typeof rt.html === 'string' ? rt.html : '';
    const lines = html
        .replace(/<\/(p|div|li)>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .split('\n')
        .map((l) => l.replace(/&nbsp;/g, ' ').trim())
        .filter(Boolean);
    const hasHeader = headers.some((h) => h.length > 0);
    const toRow = (cells) => {
        const settings = {};
        cells.slice(0, 6).forEach((c, i) => {
            settings[`c${i + 1}`] = c;
        });
        return { id: nanoid(), type: 'size-row', version: block.version, settings };
    };
    const rows = [];
    if (hasHeader)
        rows.push(toRow(headers));
    for (const line of lines) {
        const cells = splitCsv(line);
        if (cells.some((c) => c.length > 0))
            rows.push(toRow(cells));
    }
    if (rows.length === 0)
        return block;
    const { header1: _h1, header2: _h2, header3: _h3, header4: _h4, tableHeaderContent: _thc, tableContent: _tc, ...rest } = bs;
    const settings = { ...rest };
    // Header-less legacy tables keep rendering header-less: the first migrated row is DATA.
    if (!hasHeader)
        settings.firstRowHeader = false;
    return { ...block, settings, blocks: [...(block.blocks ?? []), ...rows] };
}
/**
 * `featured-product` v3 → v4: the MEDIA SLOT becomes the product gallery.
 *
 * v3 shipped a standalone `image` block as the media slot (an upload the seller never made → a scenic
 * placeholder), so picking a product showed NONE of its photos, and — because only `media-gallery` renders
 * in the media column — the section stacked into ONE column instead of the product page's two.
 *
 * On every hydrate (builder + storefront) a featured-product still on v3 whose media slot is an EMPTY
 * `image` block (no upload) has that block swapped for a `media-gallery` carrying the product-page gallery
 * settings: the picked product's images, thumbnails and lightbox, in the two-column layout.
 *
 * Deliberately conservative — it leaves the section alone when the seller has an image of their own in that
 * block (their content is never thrown away) or already has a `media-gallery`, which also makes it
 * idempotent: after the swap the section has a gallery, so a re-hydrate changes nothing.
 */
function migrateFeaturedProductMedia(section) {
    if (section.type !== 'featured-product')
        return section;
    if ((section.version ?? 0) >= FEATURED_PRODUCT_GALLERY_VERSION)
        return section;
    const blocks = section.blocks ?? [];
    if (blocks.some((b) => b.type === 'media-gallery'))
        return section;
    const legacy = blocks.findIndex((b) => b.type === 'image' && !b.settings?.image);
    const slot = legacy < 0 ? undefined : blocks[legacy];
    if (!slot)
        return section;
    const next = blocks.slice();
    next[legacy] = { ...slot, type: 'media-gallery', settings: { ...FEATURED_GALLERY_SETTINGS } };
    return { ...section, blocks: next };
}
/**
 * FOOTER v3 (2026-09-21): the copyright line left the section fields for a block of its own, so the
 * seller can click it in the canvas or the rail and write what she wants — "© 2026 aquilixShop. Tous
 * droits réservés." was OUR sentence on HER store, and a footer saved before 17/09 still carries it in
 * `settings.copyright` (the schema default changed that day, which reaches no stored document). On every
 * hydrate (builder + storefront) a footer whose settings still hold a non-empty `copyright` and that has
 * no `copyright` block yet gets one, carrying her text verbatim; the setting is dropped either way so a
 * later save writes the v3 shape. Idempotent: once the block is there the section is left alone.
 */
function migrateFooterCopyright(section) {
    if (section.type !== 'footer')
        return section;
    const settings = section.settings;
    if (!('copyright' in settings))
        return section;
    const { copyright, ...rest } = settings;
    const blocks = section.blocks ?? [];
    const hasBlock = blocks.some((b) => b.type === 'copyright');
    const text = typeof copyright === 'string' ? copyright.trim() : '';
    const nextBlocks = !hasBlock && text
        ? [
            ...blocks,
            { id: nanoid(), type: 'copyright', version: 3, settings: { copyright: text } },
        ]
        : blocks;
    return { ...section, settings: rest, blocks: nextBlocks };
}
/**
 * `mobileAlignment` has TWO vocabularies under ONE field name: `heading` stores `mobile-left|mobile-center|
 * mobile-right`, every other block (`text`, `title`, `image`, `buy-buttons`, `emoji-benefits`,
 * `text-with-icon`) stores `left|center|right`. The image-with-text / rich-text / video-with-text presets
 * copied the heading value onto the `text` block, so a fresh section carried `mobile-center` on a block
 * whose enum has no such value: the component wrote `text-align:mobile-center`, the browser dropped the
 * invalid rule and the paragraph sat LEFT while the inspector showed « Centre » (founder report,
 * 2026-09-21). Picking Left then Center re-wrote a legal `center`, which is how it « came back ».
 *
 * The presets are fixed, but every store that added one of those sections before the fix still stores
 * the prefixed value. Strip the prefix on the blocks whose enum is the bare one — idempotent, and a
 * value already legal is untouched. `heading` is NOT in the set: its enum is the prefixed one, and its
 * reader already accepts both.
 */
const BARE_MOBILE_ALIGN_BLOCKS = new Set([
    'text',
    'title',
    'image',
    'buy-buttons',
    'emoji-benefits',
    'text-with-icon',
]);
function migrateMobileAlignmentVocabulary(block) {
    if (!BARE_MOBILE_ALIGN_BLOCKS.has(block.type))
        return block;
    const v = block.settings?.mobileAlignment;
    if (typeof v !== 'string' || !v.startsWith('mobile-'))
        return block;
    const bare = v.slice('mobile-'.length);
    if (bare !== 'left' && bare !== 'center' && bare !== 'right')
        return block;
    return { ...block, settings: { ...block.settings, mobileAlignment: bare } };
}
/** Apply the block-level structural migrations recursively down a block tree (React-free, pure). */
function migrateBlockTree(block) {
    const migrated = migrateMobileAlignmentVocabulary(migrateSizingChart(migrateBenefitPanel(block)));
    if (migrated.blocks && migrated.blocks.length > 0) {
        return { ...migrated, blocks: migrated.blocks.map(migrateBlockTree) };
    }
    return migrated;
}
/**
 * Hydrate a stored document for rendering: per section, run resolveSettings (migrate + defaults),
 * remap renamed theme tokens (0019 §4a) across section + nested-block settings, and default blocks to [].
 * UNKNOWN section types are kept untouched so the renderer can decide (drop in 'live', labelled
 * placeholder in 'edit') — a document authored against a newer registry degrades gracefully instead of
 * white-screening (the direct fix for Shopyan's brittle renderer).
 */
export function hydrateDocument(doc, registry) {
    // A document is UNTRUSTED input (authored in the builder, fetched over the wire). A null/typeless
    // section, a non-array `sections`, or a `resolveSettings` that throws must DROP/degrade that one node —
    // never throw and blank the WHOLE page. This runs before the per-section error boundaries, so an
    // unguarded throw here is precisely Shopyan's brittle-renderer failure mode (CLAUDE.md): one bad node
    // white-screens the store. Valid sections are unaffected.
    const sections = Array.isArray(doc?.sections) ? doc.sections : [];
    return {
        ...doc,
        sections: sections
            .filter((s) => !!s && typeof s === 'object' && typeof s.type === 'string')
            .map((s) => {
            const def = registry[s.type];
            if (!def)
                return s; // unknown type → kept; the renderer's FallbackSection handles it
            try {
                const settings = remapColorTokens(resolveSettings(def.schema, s, doc?.locale));
                const blocks = (s.blocks ?? []).map((b) => migrateBlockTree(remapColorTokens(b)));
                return migrateFooterCopyright(migrateFeaturedProductMedia({ ...s, settings, blocks }));
            }
            catch {
                return { ...s, blocks: s.blocks ?? [] }; // bad migrate/defaults → render raw, never crash
            }
        }),
    };
}
//# sourceMappingURL=hydrate.js.map